import React, {useState} from 'react';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import {API_BASE_URL} from "../../config";
import axios from "axios";
import {useSnackbar} from "notistack";
import PasswordField from "../../components/passwords/PasswordField";
import PasswordRequirements from "../../components/passwords/PasswordRequirements";
import usePasswordInputGuard from "../../components/passwords/usePasswordInputGuard";
import { isValidNewPassword } from "../../utils/passwordPolicy";
import { getStudyLogo } from "../../utils/studyLogo";
import "../../stylesheets/passwordReset.css";

function Profile() {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const {enqueueSnackbar} = useSnackbar();
    const { changePassword, handlePasswordPaste } = usePasswordInputGuard();
    const [isSubmitting, setIsSubmitting] = useState(false);

    let currentUser = JSON.parse(localStorage.getItem("user"));
    const studyLogo = getStudyLogo(currentUser);
    const [logoFailed, setLogoFailed] = useState(false);

    let [textfieldCurrentPass, setTextfieldCurrentPass] = useState("")
    let [textfieldNewPassCheck, setTextfieldNewPassCheck] = useState("")
    let [textfieldNewPass, setTextfieldNewPass] = useState("")

    function CheckPass() {
        if (isSubmitting) return;
        if (!textfieldCurrentPass || !textfieldNewPass || !textfieldNewPassCheck) {
            enqueueSnackbar('Merci de renseigner tous les champs.', { variant: 'error' });
            return;
        }
        if (textfieldNewPass !== textfieldNewPassCheck) {
            enqueueSnackbar('Vos mots de passes ne correspondent pas.', {
                variant: 'error'
            });
            return;
        }
        if (!isValidNewPassword(textfieldNewPass)) {
            enqueueSnackbar('Le mot de passe ne correspond pas aux critères requis.', { variant: 'error' });
            return;
        }
        ChangePassword();
    }

    function ChangePassword() {
        setIsSubmitting(true);
        let dataObject = {
            "login": currentUser.login,
            "password": textfieldCurrentPass,
            "new_password": textfieldNewPass
        }

        var config = {
            method: 'post',
            url: `${API_BASE_URL}/users`,
            headers: {
                'Content-Type': 'text/plain',
                'Authorization': JSON.parse(localStorage.getItem("user"))?.jwt,
            },
            data: dataObject
        };
        axios(config)
            .then((response) => {
                if (response.status === 200) {
                    handleClose()
                } else {
                    enqueueSnackbar('Une erreur est survenue', {
                        variant: 'error'
                    });
                }
            })
            .catch((error) => {
                // 1. Gestion spécifique du 401 / Autre utilisateur connecté
                if (error.response?.status === 401 && error.response.data != null) {
                    if (error.response.data.error == "Other_user_connected" ) {
                        localStorage.removeItem("user")
                        localStorage.setItem("other_user_connected", "other_user_connected")
                        window.location.href = "/login"
                        return;
                    } else {
                        localStorage.removeItem("user");
                        window.location.href = "/login";
                        enqueueSnackbar('Session expirée', {variant: 'error'});
                        return;
                    }
                }

                // 2. Gestion générique de toutes les autres erreurs
                enqueueSnackbar('Une erreur est survenue', {
                    variant: 'error'
                });
            })
            .finally(() => setIsSubmitting(false));
    }


    const details = [
        { label: "Nom", value: currentUser.lastname },
        { label: "Prénom", value: currentUser.firstname },
        { label: "Société", value: currentUser.societe },
    ];

    return (
        <main className="password-reset-page">
            <div className="password-reset-content password-reset-content--wide">
                <section className="password-reset-card" aria-labelledby="profile-title">
                    <h1 id="profile-title">Mon compte</h1>

                    <dl className="password-reset-details">
                        {details.map(({ label, value }) => (
                            <div key={label}>
                                <dt>{label}</dt>
                                <dd>{value || "-"}</dd>
                            </div>
                        ))}
                        <div>
                            <dt>Logo de l’étude</dt>
                            <dd>
                                {studyLogo && !logoFailed ? (
                                    <img
                                        className="password-reset-logo"
                                        src={studyLogo}
                                        alt="Logo de l’étude"
                                        onError={() => setLogoFailed(true)}
                                    />
                                ) : "Aucun logo disponible"}
                            </dd>
                        </div>
                    </dl>

                    <section className="password-reset-section" aria-labelledby="profile-password-title">
                        <h2 id="profile-password-title">Mot de passe</h2>
                        <button type="button" className="password-reset-button password-reset-button--inline" onClick={handleOpen}>
                            Changer mon mot de passe
                        </button>
                    </section>
                </section>

                <footer className="password-reset-support">
                    <p>Pour plus d’informations, contactez-nous:</p>
                    <div>
                        <a href="mailto:support@langloys.com">support@langloys.com</a>
                        <span aria-hidden="true">·</span>
                        <a href="tel:+33491251338">04 91 25 13 38</a>
                    </div>
                </footer>
            </div>

            <Modal
                keepMounted
                open={open}
                onClose={handleClose}
                aria-labelledby="profile-password-modal-title"
            >
                <form className="password-reset-card password-reset-modal password-reset-modal--centered" onSubmit={(event) => { event.preventDefault(); CheckPass(); }} aria-busy={isSubmitting}>
                    <button type="button" className="password-reset-close" aria-label="Fermer" onClick={handleClose}>
                        <CloseIcon fontSize="small" />
                    </button>

                    <h2 id="profile-password-modal-title">Modifier mon mot de passe actuel</h2>

                    <div className="password-reset-form">
                        <PasswordRequirements password={textfieldNewPass} id="profile-password-rules" />

                        <PasswordField
                            id="profile-current-password"
                            label="Ancien mot de passe"
                            autoComplete="current-password"
                            visibilityLabel="l’ancien mot de passe"
                            disabled={isSubmitting}
                            value={textfieldCurrentPass}
                            onChange={setTextfieldCurrentPass}
                        />

                        <PasswordField
                            id="profile-new-password"
                            label="Nouveau mot de passe"
                            visibilityLabel="le nouveau mot de passe"
                            describedBy={textfieldNewPass ? "profile-password-rules" : undefined}
                            disabled={isSubmitting}
                            value={textfieldNewPass}
                            onPaste={handlePasswordPaste}
                            onChange={(value) => changePassword(setTextfieldNewPass, value)}
                        />

                        <PasswordField
                            id="profile-confirm-password"
                            label="Vérification nouveau mot de passe"
                            visibilityLabel="la vérification du mot de passe"
                            describedBy={textfieldNewPass ? "profile-password-rules" : undefined}
                            disabled={isSubmitting}
                            value={textfieldNewPassCheck}
                            onPaste={handlePasswordPaste}
                            onChange={(value) => changePassword(setTextfieldNewPassCheck, value)}
                        />

                        <button type="submit" className="password-reset-button" disabled={isSubmitting}>
                            {isSubmitting ? 'Modification en cours…' : 'Changer mon mot de passe'}
                        </button>
                    </div>
                </form>
            </Modal>
        </main>
    );
};

export default Profile;
